import React, { useState } from 'react'

import { Dropdown } from '@/components/ui'

import { SaveMapModal, DeleteMapModal } from './/MapModals'
import { AssetListShared } from './AssetList'
import { AssetQuickView } from './AssetQuickView'
import { AlarmCodeFilter, AlertsFilter, DoorFilter, GatewayFilter, GeofenceFilter, PowerFilter } from './filters'
import { useAssetList, useMapControls } from './hooks/useMapControls'
import { useMapContext } from './MapContext'
import type { BottomNavMobileControlsProps } from './types'

type SheetType = 'list' | 'filters' | 'maps' | null

function BottomNavMobileControls({
  activeFilter,
  setActiveFilter,
  mapRef,
  setCurrentAssetId,
}: BottomNavMobileControlsProps) {
  const { selectedSavedMap } = useMapContext()
  const [activeSheet, setActiveSheet] = useState<SheetType>(null)
  const { assets, context, handleAssetClick } = useAssetList(mapRef, setCurrentAssetId)
  const controls = useMapControls(mapRef)

  const hasSelectedAsset = context.currentAssetId !== undefined

  const openSheet = (type: SheetType) => {
    setActiveSheet((prev) => (prev === type ? null : type))
  }

  const onClose = () => setActiveFilter(undefined)

  return (
    <div className="md:hidden">
      <div
        className={`w-full bottom-[45px] h-[800px] transition-transform duration-300 ease-out bg-white rounded-t-2xl z-50 fixed md:hidden ${activeSheet ? 'translate-y-0' : 'translate-y-full'}`}
      >
        <div className="w-full h-full flex flex-col">
          {/* Render different content based on activeSheet */}
          {activeSheet === 'list' && (
            <>
              {/* Header - shrink-0 keeps it from being compressed */}
              <div className="px-4 py-2 border-b border-black/30 shrink-0">
                <h3 className="text-xl font-semibold">Assets</h3>
                <div className="text-sm font-light">{assets.length.toLocaleString()} assets</div>
              </div>

              {/* Quick View Panel - only takes space when asset is selected */}
              {hasSelectedAsset && (
                <div className="shrink-0 border-b border-base-300">
                  <AssetQuickView
                    variant="inline"
                    onClose={() => setCurrentAssetId(undefined, 'quickview-close')}
                    onShowDetails={() => context.setShowAssetModal(true)}
                  />
                </div>
              )}

              {/* Scrollable Asset List - flex-1 fills remaining space */}
              <div className="flex-1 overflow-y-auto min-h-0">
                {context.filters.companyId && !context.isLoadingAssets && (
                  <AssetListShared
                    assets={assets}
                    currentAssetId={context.currentAssetId}
                    onAssetClick={handleAssetClick}
                    onShowDetails={() => context.setShowAssetModal(true)}
                    viewMode="list"
                  />
                )}

                {!context.filters.companyId && (
                  <div className="flex-1 flex items-center justify-center p-4">
                    <span className="text-sm text-base-content/70 text-center">Select a company to view assets</span>
                  </div>
                )}

                {context.isLoadingAssets && (
                  <div className="flex-1 flex items-center justify-center">
                    <span className="loading loading-spinner loading-lg"></span>
                  </div>
                )}
              </div>
            </>
          )}

          {activeSheet === 'filters' && (
            <>
              <div className="px-4 py-2 border-b border-black/30 shrink-0">
                <h3 className="text-xl font-semibold">Map Filters</h3>
              </div>
              <div className="flex flex-col w-full justify-start items-start gap-8 p-4 h-full overflow-y-scroll">
                <GeofenceFilter onSelect={onClose} />
                <PowerFilter onSelect={onClose} />
                <GatewayFilter onSelect={onClose} />
                <DoorFilter onSelect={onClose} />
                <AlarmCodeFilter
                  isOpen={activeFilter === 'alarmCodes'}
                  onToggle={() => setActiveFilter(activeFilter === 'alarmCodes' ? undefined : 'alarmCodes')}
                />
                <AlertsFilter
                  isOpen={activeFilter === 'alerts'}
                  onToggle={() => setActiveFilter(activeFilter === 'alerts' ? undefined : 'alerts')}
                />
              </div>
            </>
          )}

          {activeSheet === 'maps' && (
            <div className="p-4">
              <label className="text-xs text-base-content/70 block mb-2">Saved Map</label>
              <Dropdown
                options={controls.savedMapOptions}
                selectedItem={controls.selectedSavedMapOption}
                handleSingleSelectItem={controls.handleSavedMapChange}
                placeholder="Select saved map..."
              />
            </div>
          )}
        </div>
      </div>

      <div className="text-black fixed bottom-0 z-50 flex w-full justify-between h-[45px] bg-base-100 border-t">
        <button onClick={() => openSheet('maps')} className={`w-1/3 ${activeSheet === 'maps' ? 'font-bold' : ''}`}>
          Saved
        </button>
        <button
          onClick={() => openSheet('filters')}
          className={`w-1/3 ${activeSheet === 'filters' ? 'font-bold' : ''}`}
        >
          Filter
        </button>
        <button onClick={() => openSheet('list')} className={`w-1/3 ${activeSheet === 'list' ? 'font-bold' : ''}`}>
          View
        </button>
      </div>

      <SaveMapModal {...controls} selectedSavedMap={selectedSavedMap} />
      <DeleteMapModal {...controls} selectedSavedMap={selectedSavedMap} />
    </div>
  )
}

export default BottomNavMobileControls
